import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiRequest } from 'utils/api';

// ==============================|| INITIAL STATE ||============================== //

interface PricingPlan {
  id: string;
  code: string;
  displayName: string;
  monthlyPriceUsd: string | null; // Comes as string from PostgreSQL decimal type
  annualPriceUsd: string | null; // Comes as string from PostgreSQL decimal type
  includedAcu: number;
  pricePerAcu: number | null;
  metadata?: {
    popular?: boolean;
    features?: string[];
    recommended?: string;
    contactSales?: boolean;
  };
}

interface PricingState {
  plans: PricingPlan[];
  status: 'idle' | 'loading' | 'success' | 'failed';
  error: string;
}

const initialState: PricingState = {
  plans: [],
  status: 'idle',
  error: '',
};

// ==============================|| ASYNC THUNKS ||============================== //

/**
 * Fetch all active pricing plans (public endpoint, no auth required)
 */
export const fetchPricingPlans = createAsyncThunk(
  'pricing/fetchPlans',
  async (_, { rejectWithValue }) => {
    try {
      const data = await apiRequest('/pricing/plans');
      // API returns data directly (not wrapped in response for this endpoint)
      return data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch pricing plans');
    }
  }
);

// ==============================|| SLICE ||============================== //

const pricingSlice = createSlice({
  name: 'pricing',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = '';
    },
    resetPricingState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Fetch pricing plans
      .addCase(fetchPricingPlans.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(fetchPricingPlans.fulfilled, (state, action) => {
        state.status = 'success';
        // API returns { response: [...plans...], metadata: {...} }
        state.plans = action.payload.response || action.payload;
      })
      .addCase(fetchPricingPlans.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
      });
  },
});

// ==============================|| EXPORTS ||============================== //

export const { clearError, resetPricingState } = pricingSlice.actions;

// Selectors
export const selectPricingPlans = (state: any) => state.pricing.plans;
export const selectPricingStatus = (state: any) => state.pricing.status;
export const selectPricingError = (state: any) => state.pricing.error;

export default pricingSlice.reducer;
